import { useMemo } from "react";
import useTenantContextStore from "@stores/shared/useTenantContextStore";
import { parseThemeValues } from "@theme/shared/theme-values";
import { isActiveDirectoryMode } from "@config/shared/authentication-modes";

/**
 * Read the tenant context (loaded once at startup — see useTenantContextStore)
 * for UI: the tenant name, its logo (the resolved `mediaUrl`, fetchable),
 * the parsed branding map, and how the tenant signs in.
 *
 * Every field is safe to use before/without a loaded context: `name`/`logoUrl`
 * are null (callers show the Oxygen fallback) and `branding` is {} (the theme
 * falls back to Oxygen defaults). `isReady` is true once the fetch has settled.
 *
 * `theme` (a stable string) is selected — not the parsed object — so the memo
 * key is stable and getSnapshot stays cached; parsing happens in useMemo.
 */
export const useTenantBranding = () => {
  const status = useTenantContextStore((s) => s.status);
  const name = useTenantContextStore((s) => s.name);
  const logoUrl = useTenantContextStore((s) => s.mediaUrl);
  const theme = useTenantContextStore((s) => s.theme);
  const authenticationMode = useTenantContextStore((s) => s.authenticationMode);

  const branding = useMemo(() => parseThemeValues(theme), [theme]);

  return {
    status,
    isReady: status === "ready" || status === "error",
    name,
    logoUrl,
    branding,
    authenticationMode,
    isActiveDirectory: isActiveDirectoryMode(authenticationMode),
  };
};
